import { Scale, Salad, Moon, Lightbulb, ArrowRight } from 'lucide-react';
import SpotCounter from './SpotCounter';
import TrustBadges from './TrustBadges';
import PremiumCTA from './PremiumCTA';

export default function Hero7TageBodyBoost() {
  return (
    <section className="relative bg-gradient-to-br from-sage-50 via-mint-50 to-lavender-50 overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 right-20 w-72 h-72 bg-mint-200 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-lavender-200 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        {/* Main Headline - Full Width on Mobile, Hidden on Desktop */}
        <div className="mb-12 lg:hidden">
          <h1 className="text-4xl sm:text-5xl font-bold text-charcoal-900 leading-tight">
            Spürbar leichter & klarer in{' '}
            <span className="text-sage-600 block mt-2">7 Tagen</span>
          </h1>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-end">
          {/* Left Column - Headline (Desktop), Sub-headlines, CTA and Social Proof */}
          <div className="flex flex-col justify-end space-y-8">
            {/* Main Headline - Only Visible on Desktop */}
            <h1 className="hidden lg:block text-4xl sm:text-5xl lg:text-6xl font-bold text-charcoal-900 leading-tight">
              Spürbar leichter & klarer in{' '}
              <span className="text-sage-600 block mt-2">7 Tagen</span>
            </h1>

            {/* Sub-headlines */}
            <div className="space-y-6">
              <p className="text-2xl sm:text-3xl text-charcoal-700 leading-relaxed font-medium">
                Mit Bewegung, Ernährung, Schlaf & Mindset in Balance kommen
              </p>

              <p className="text-xl text-charcoal-600 leading-relaxed">
                In 7 Tagen raus aus dem Energiemangel – hin zu klarem Kopf, besserem Schlaf und mehr Leichtigkeit.
              </p>
            </div>

            {/* Spot Counter */}
            <div className="mb-4">
              <SpotCounter variant="compact" showProgressBar={true} />
            </div>

            {/* CTA Button */}
            <div className="space-y-4">
              <PremiumCTA
                href="https://www.digistore24.com/product/642539/siebentage5kilo/?ds24_domain=www.digistore24.com&voucher=7TageStart"
                size="lg"
              >
                Jetzt in Balance kommen
              </PremiumCTA>

              <TrustBadges variant="compact" showLabels={true} />

              <p className="text-sm text-charcoal-600 flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-mint-500 rounded-full animate-pulse" />
                Sofortiger Zugang • 7-Tage-Geld-zurück-Garantie
              </p>
            </div>

            {/* Social Proof */}
            <div className="flex items-center gap-6 pt-6 border-t border-charcoal-200/50">
              <div className="flex -space-x-3">
                <div className="w-10 h-10 rounded-full bg-sage-300 border-2 border-white" />
                <div className="w-10 h-10 rounded-full bg-mint-300 border-2 border-white" />
                <div className="w-10 h-10 rounded-full bg-lavender-300 border-2 border-white" />
                <div className="w-10 h-10 rounded-full bg-peach-300 border-2 border-white flex items-center justify-center text-xs font-semibold text-charcoal-700">
                  1K+
                </div>
              </div>
              <div className="text-sm">
                <div className="font-semibold text-charcoal-900">Über 1.000 zufriedene Teilnehmer</div>
                <div className="text-charcoal-600 flex items-center gap-1">
                  <span className="text-cta-orange">★★★★★</span>
                  <span>4.9/5 Bewertung</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Image with Icons */}
          <div className="relative flex items-end justify-center lg:justify-end">
            {/* Hero Image */}
            <div className="relative z-10">
              <img
                src="/herofrau nobg.png"
                alt="Frau in entspannter Pose"
                className="w-full max-w-md lg:max-w-lg h-auto object-contain"
              />
            </div>

            {/* Icons positioned left of the woman */}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 space-y-6 hidden lg:block">
              <div className="flex items-center gap-5 bg-white/90 backdrop-blur-sm px-6 py-5 rounded-2xl shadow-lg min-w-[200px]">
                <div className="flex-shrink-0 w-14 h-14 bg-sage-100 rounded-full flex items-center justify-center">
                  <Scale className="w-7 h-7 text-sage-600" />
                </div>
                <span className="font-bold text-lg text-charcoal-800">Balance</span>
              </div>

              <div className="flex items-center gap-5 bg-white/90 backdrop-blur-sm px-6 py-5 rounded-2xl shadow-lg min-w-[200px]">
                <div className="flex-shrink-0 w-14 h-14 bg-mint-100 rounded-full flex items-center justify-center">
                  <Salad className="w-7 h-7 text-mint-600" />
                </div>
                <span className="font-bold text-lg text-charcoal-800">Ernährung</span>
              </div>

              <div className="flex items-center gap-5 bg-white/90 backdrop-blur-sm px-6 py-5 rounded-2xl shadow-lg min-w-[200px]">
                <div className="flex-shrink-0 w-14 h-14 bg-lavender-100 rounded-full flex items-center justify-center">
                  <Moon className="w-7 h-7 text-lavender-600" />
                </div>
                <span className="font-bold text-lg text-charcoal-800">Schlaf</span>
              </div>

              <div className="flex items-center gap-5 bg-white/90 backdrop-blur-sm px-6 py-5 rounded-2xl shadow-lg min-w-[200px]">
                <div className="flex-shrink-0 w-14 h-14 bg-peach-100 rounded-full flex items-center justify-center">
                  <Lightbulb className="w-7 h-7 text-peach-600" />
                </div>
                <span className="font-bold text-lg text-charcoal-800">Mindset</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
